import { useMemo, type ReactNode } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { ComboSelect } from '../../../components/ComboSelect';
import { formatCombo, effectiveCombo } from '../../../components/hotkeys';
import type { PlmExamInput, PlmFiberTypeValue, PlmLookups } from './plm.api';
import type { PointCountType } from './plmPointCount';
import { applyFiberTypeValues, findFiberTypeValue, namesForFiberType } from './plmAutofill';
import { PlmNameInput } from './PlmDeviceFields';

/**
 * The "PLM Exam" tab — the grid of fibers / components found in this layer
 * (Component · Name · Result) above the six optical-property groups
 * (Birefringence · Pleochroism · Morphology · Sign of Elongation · Extinction ·
 * Refractive Index).
 *
 * Owner feedback 2026-10-07 (already in the legacy PLM form): the Fiber Color
 * column and the Dispersion Staining Colors group are not required and are not
 * drawn. Their columns still round-trip — a row saved with either keeps it.
 *
 * Two behaviours carry over from legacy and both live in `plmAutofill.ts`:
 *  • the Name combo CASCADES off Component — each fiber type draws its options
 *    from a different settings master (Grid_Load :1264);
 *  • picking a Name AUTOFILLS the optical properties from dbo.FiberTypesValues
 *    (LoadPLMEDVal :3292), additively.
 */

/** The first column's caption as it reads inside a sentence (user feedback C5, 2026-11-02). */
const ROW_WORD = 'fiber / material';

export interface PlmExamTabProps {
  exams: PlmExamInput[];
  onChange: (next: PlmExamInput[]) => void;
  activeIndex: number;
  onActiveIndexChange: (i: number) => void;
  lookups: PlmLookups;
  fiberTypeValues: PlmFiberTypeValue[];
  readOnly: boolean;

  // ── point count (Phase 2) ────────────────────────────────────────────────
  /** The stored count's type, or null when this layer has none. */
  pointCountType: PointCountType | null;
  /** Switches the count on (picking a type) or off (null → clears it). */
  onPointCountTypeChange: (next: PointCountType | null) => void;
  /** Opens the PC Entry grid. */
  onOpenPointCount: () => void;
  /** Add / remove a component row — the + Add / Remove pair above the grid.
   *
   *  `onAddExamAt` / `onRemoveExamAt` / `onMoveExam` are still on this contract
   *  and still implemented by the page, but are NOT wired to any control here:
   *  the prototype's grid has no per-row Actions column and no reorder arrows,
   *  so those affordances were removed (2026-09-25). Kept on the interface
   *  because the handlers are sound and re-exposing them is a one-line change
   *  if the lab wants insert-at-position back. */
  onAddExam: (init?: Pick<PlmExamInput, 'fiberTypeOid'>) => void;
  onRemoveExam: () => void;
  /** Per-ROW actions (the Actions column) and reorder, 2026-09-24. Distinct
   *  from the toolbar pair, which always act on the ACTIVE row. */
  onAddExamAt: (i: number) => void;
  onRemoveExamAt: (i: number) => void;
  onMoveExam: (i: number, dir: -1 | 1) => void;
  /** Disabled until the layer has been saved once — the count hangs off the
   *  observation row, which does not exist until then. */
  pointCountDisabled: boolean;
  /** Asbestos fibers whose Result was WRITTEN BY the point count. Those cells
   *  are read-only and wear the "PC" tag (prototype v8) — the number is edited
   *  through PC Entry, not by hand. Empty / omitted when there is no count. */
  pointCountFiberNames?: ReadonlySet<string>;
  /** Shown on the toolbar after PC Entry — the Contamination Check box and its
   *  50-layer badge (G5 parity 2026-10-15). */
  toolbarExtra?: ReactNode;
  /** The lab's RI oils (dbo.AI_PlmUtility Kind 'Oil', G5 parity 2026-10-16): RI Oil
   *  offers them while free typing is kept; a new name is remembered on Save. */
  oilNames?: readonly string[];
}

type BoolKey = {
  [K in keyof PlmExamInput]: PlmExamInput[K] extends boolean ? K : never
}[keyof PlmExamInput];

type TextKey = {
  [K in keyof PlmExamInput]: PlmExamInput[K] extends string | null ? K : never
}[keyof PlmExamInput];

const BIREFRINGENCE: ReadonlyArray<readonly [BoolKey, string]> = [
  ['typeLow', 'Low'], ['typeMedium', 'Medium'], ['typeHigh', 'High'],
  ['typeIsotropic', 'Isotropic'],
];

const MORPHOLOGY: ReadonlyArray<readonly [BoolKey, string]> = [
  ['morphologyBundles', 'Bundles'], ['morphologyCurved', 'Curved'],
  ['morphologyRibbon', 'Ribbon'],   ['morphologySplayed', 'Splayed'],
  ['morphologyStraight', 'Straight'], ['morphologyWavey', 'Wavy'],
  ['morphologyUniform', 'Uniform'],
];

const ELONGATION: ReadonlyArray<readonly [BoolKey, string]> = [
  ['elongationPositive', 'Positive'], ['elongationNegative', 'Negative'],
  ['elongationBoth', 'Both'],
];

const EXTINCTION: ReadonlyArray<readonly [BoolKey, string]> = [
  ['extinctionOblique', 'Oblique'], ['extinctionParallel', 'Parallel'],
  ['extinctionUndulose', 'Undulose'], ['extinctionIncomplete', 'Incomplete'],
  ['extinctionNone', 'None'],
];

export function PlmExamTab({
  exams, onChange, activeIndex, onActiveIndexChange,
  lookups, fiberTypeValues, readOnly,
  pointCountType, onPointCountTypeChange, onOpenPointCount, pointCountDisabled,
  pointCountFiberNames,
  onAddExam, onRemoveExam, toolbarExtra, oilNames = [],
}: PlmExamTabProps) {
  const active = exams[activeIndex];

  // Which component's optics are on screen (prototype v8 `#opticsFor`). The
  // panel is ONE set bound to the SELECTED row, so a sample with Chrysotile +
  // Amosite keeps a separate set per fibre — without the caption there is
  // nothing on screen saying which one you are editing.
  const opticsFor = active?.name?.trim()
    ? `for ${active.name.trim()}`
    : active ? `for the selected ${ROW_WORD}` : `— select a ${ROW_WORD}`;

  // The textbook properties for this fibre, from dbo.FiberTypesValues — the
  // SAME master the pick-a-name autofill reads, so the hint can never disagree
  // with what autofill wrote. DISPLAY ONLY: it never writes to the row (the
  // analyst records what they SAW, and a hint that silently became data would
  // be a fabricated observation).
  const typical = (() => {
    const v = findFiberTypeValue(fiberTypeValues, active?.name ?? null);
    if (!v) return null;
    const parts = [v.morphology, v.birefringence, v.extinction, v.elongation]
      .map(x => x?.trim()).filter(Boolean) as string[];
    const ri = [v.riGamma?.trim() ? `γ ${v.riGamma.trim()}` : null,
                v.riAlpha?.trim() ? `α ${v.riAlpha.trim()}` : null]
      .filter(Boolean).join(' / ');
    if (ri) parts.push(ri);
    return parts.length ? parts.join(' · ') : null;
  })();

  /**
   * Adds a component row with its Component (FiberTypes) already set — the
   * prototype's inline quick-add. The type goes IN the add, so the row is born
   * with it; its Name list then cascades from it exactly as after a manual pick
   * (a new row has no name to keep or clear).
   *
   * It used to add a blank row and set the type a frame later through
   * `onFiberTypeChange` — but that was the closure of the render BEFORE the
   * add, whose `exams` did not have the new row yet, so it found no row and
   * returned: every quick-add landed an EMPTY "Select…" row (owner report
   * 2026-09-29).
   */
  const addWithType = (fiberTypeOid: string) => onAddExam({ fiberTypeOid });

  const fiberTypeName = (oid: string | null) =>
    lookups.fiberTypes.find(t => t.id === oid)?.name ?? null;

  /** The components' running total. Legacy stores Result as free text ("<1",
   *  "Trace"), so anything non-numeric is simply not counted — the total is of
   *  the quantities, and a layer made only of trace readings honestly has no
   *  total to show. */
  const examTotal = (() => {
    const nums = exams
      .map(e => Number.parseFloat((e.result ?? '').replace(/[<%\s]/g, '')))
      .filter(n => Number.isFinite(n));
    if (!nums.length) return { text: '—', balanced: true };
    const sum = Number(nums.reduce((a, b) => a + b, 0).toFixed(2));
    return { text: `${sum}%`, balanced: Math.abs(sum - 100) < 0.01 };
  })();

  /** What this layer reports: its asbestos components, or "None Detected".
   *  Asbestos is what the report and the QC comparison key on, so that is what
   *  the bar states — a list of every component would just restate the grid. */
  const layerResult = (() => {
    const asb = exams.filter(e => fiberTypeName(e.fiberTypeOid) === 'Asbestos Fibers');
    if (!asb.length) return 'None Detected';
    return asb.map(e => `${e.name || '—'} ${e.result || '—'}`).join(' · ');
  })();


  const patch = (i: number, next: PlmExamInput) =>
    onChange(exams.map((e, ix) => (ix === i ? next : e)));

  const setActive = <K extends keyof PlmExamInput>(key: K, v: PlmExamInput[K]) => {
    if (!active) return;
    patch(activeIndex, { ...active, [key]: v });
  };

  /** Component changed → the Name options change with it, so a name that no
   *  longer belongs is cleared rather than left dangling (legacy resets the
   *  combo's datasource, which has the same effect). */
  const onFiberTypeChange = (i: number, oid: string) => {
    const row = exams[i];
    if (!row) return;
    const allowed = namesForFiberType(lookups.componentNames, fiberTypeName(oid || null));
    const keepName = row.name && allowed.includes(row.name) ? row.name : null;
    patch(i, { ...row, fiberTypeOid: oid || null, name: keepName });
  };

  /** Name picked → autofill the optical properties (legacy LoadPLMEDVal). */
  const onNameChange = (i: number, name: string) => {
    const row = exams[i];
    if (!row) return;
    const withName: PlmExamInput = { ...row, name: name || null };
    const values = findFiberTypeValue(fiberTypeValues, name);
    patch(i, values ? applyFiberTypeValues(withName, values) : withName);
  };

  const columns = useMemo<AppColumn<PlmExamInput>[]>(() => [
    {
      field: 'fiberType', header: 'Fiber / Material', width: 190,
      text: r => fiberTypeName(r.fiberTypeOid) ?? '',
      render: r => {
        const i = exams.indexOf(r);
        return readOnly
          ? <>{fiberTypeName(r.fiberTypeOid) ?? ''}</>
          : (
            <ComboSelect
              value={r.fiberTypeOid ?? ''}
              options={lookups.fiberTypes.map(t => ({ value: t.id, label: t.name ?? '' }))}
              onChange={v => onFiberTypeChange(i, v)}
              placeholder="Select…"
            />
          );
      },
    },
    {
      field: 'name', header: 'Type', width: 190,
      text: r => r.name ?? '',
      render: r => {
        const i = exams.indexOf(r);
        const options = namesForFiberType(lookups.componentNames, fiberTypeName(r.fiberTypeOid));
        return readOnly
          ? <>{r.name ?? ''}</>
          : (
            <ComboSelect
              value={r.name ?? ''}
              options={options}
              onChange={v => onNameChange(i, v)}
              placeholder={r.fiberTypeOid ? 'Select…' : `Pick a ${ROW_WORD} first`}
              disabled={!r.fiberTypeOid}
            />
          );
      },
    },
    {
      field: 'result', header: 'Result %', width: 110, align: 'right',
      text: r => r.result ?? '',
      render: r => {
        const i = exams.indexOf(r);
        // A Result the point count wrote is read-only here and tagged "PC" —
        // it is edited through PC Entry (prototype v8 `.pctag`).
        const asbestosTypeId = lookups.fiberTypes.find(t => t.name === 'Asbestos Fibers')?.id;
        const fromPc = !!asbestosTypeId && r.fiberTypeOid === asbestosTypeId
          && pointCountFiberNames?.has((r.name ?? '').trim()) === true;
        const tag = fromPc
          ? <span className="plm-pctag" title="From Point Count — edit via PC Entry">PC</span>
          : null;
        if (readOnly) return <>{r.result ?? ''}{tag}</>;
        return (
          <span className="plm-exam__result">
            <input
              type="text"
              className="plm-input plm-input--grid"
              value={r.result ?? ''}
              aria-label="Result percentage"
              readOnly={fromPc}
              title={fromPc ? 'From Point Count — edit via PC Entry' : undefined}
              onChange={e => patch(i, { ...r, result: e.target.value || null })}
            />
            {tag}
          </span>
        );
      },
    },
  ], [exams, lookups, fiberTypeValues, readOnly, pointCountFiberNames]);

  const check = (key: BoolKey, label: string) => (
    <label key={key} className="plm-chk">
      <input
        type="checkbox"
        checked={active?.[key] ?? false}
        disabled={readOnly || !active}
        onChange={e => setActive(key, e.target.checked as PlmExamInput[BoolKey])}
      />
      <span>{label}</span>
    </label>
  );

  /** A label : field pair in the prototype's `.rigrid` — label right-aligned
   *  in a narrow auto column, field filling the rest. Used by the Refractive
   *  Index card, whose entries are values rather than checkboxes. */
  const riField = (key: TextKey, label: string, placeholder?: string) => (
    <>
      <label>{label}</label>
      {key === 'riOil' ? (
        // G5: "RI Oil is a drop-down of the lab's oils; a value typed that is
        // not listed is added to the list on Save."
        <PlmNameInput
          id="plm-ri-oil"
          ariaLabel={label}
          placeholder={placeholder}
          names={oilNames}
          value={active?.riOil}
          disabled={readOnly || !active}
          onChange={v => setActive(key, v as PlmExamInput[TextKey])}
        />
      ) : <input
        type="text"
        className="plm-input"
        placeholder={placeholder}
        value={active?.[key] ?? ''}
        disabled={readOnly || !active}
        onChange={e => setActive(key, (e.target.value || null) as PlmExamInput[TextKey])}
      />}
    </>
  );

  /** A free-text box that only unlocks when its gate option is ticked — the
   *  prototype disables the Pleochroism colour until "Yes" and the Morphology
   *  "Other…" until Other, so the row cannot carry a description of something
   *  it does not claim. */
  const gatedText = (key: TextKey, gate: BoolKey, placeholder: string) => (
    <input
      type="text"
      className="plm-input plm-othr"
      placeholder={placeholder}
      aria-label={placeholder}
      value={active?.[key] ?? ''}
      disabled={readOnly || !active || !active[gate]}
      title={active?.[gate] ? undefined : `Tick the option to describe it`}
      onChange={e => setActive(key, (e.target.value || null) as PlmExamInput[TextKey])}
    />
  );

  return (
    <div className="plm-exam">
      {/* Prototype v8: the exam toolbar sits on the TOP EDGE of the card —
          there is no "PLM Exam" caption strip above it, because the TAB is
          already called PLM Exam and a panel header would be a second title
          drawing a box round the grid. (Same call as Observation Details.) */}
      <div className="plm-exam-sec">
      {/* Point count (Phase 2) — legacy's Point Count checkbox + type combo +
          "PC Entry" button, which sit above the exam grid. Switching the
          checkbox OFF drops the count and resets the asbestos results, exactly
          as legacy does (PointCountAction_Execute :92). */}
      <div className="plm-pc-bar">
        {/* Prototype v8 exam toolbar: "+ Add | Remove | │ Point Count …".
            Both are full buttons — `Remove` previously kept `.plm-rowbtn`, a
            fixed-width round ICON button, so once it carried a word instead of
            a "−" the text overflowed and collided with Add (owner screenshot
            2026-09-25). The reorder arrows are gone with it: the prototype has
            none, and `sort` follows the row order the analyst types. */}
        <div className="plm-exam__acts-bar">
          <button
            type="button"
            className="ca-btn ca-btn--sm"
            onClick={() => onAddExam()}
            disabled={readOnly}
            title={`Add a ${ROW_WORD} row`}
          ><span className="tb-i tb-i-green" aria-hidden="true">＋</span> Add</button>
          <button
            type="button"
            className="ca-btn ca-btn--sm"
            onClick={onRemoveExam}
            disabled={readOnly || !active}
            title={`Remove the selected ${ROW_WORD} row`}
          ><span className="tb-i tb-i-red" aria-hidden="true">🗑</span> Remove</button>
          <span className="plm-exam__vsep" aria-hidden="true" />
        </div>

        <label className="plm-chk">
          <input
            type="checkbox"
            checked={pointCountType !== null}
            disabled={readOnly || pointCountDisabled}
            onChange={e => onPointCountTypeChange(e.target.checked ? '400' : null)}
          />
          <span>Point Count</span>
        </label>

        <label className="plm-pc-bar__type">
          <span>Type</span>
          {/* NA · 400 · 1000 — the bare counts, no "points" suffix (owner
              request 2026-09-26; the TYPE caption already says what they are).
              NA shows (disabled) when Point Count is off; the checkbox turns it on. */}
          <select
            className="plm-input"
            value={pointCountType ?? 'NA'}
            disabled={readOnly || pointCountDisabled || pointCountType === null}
            onChange={e => onPointCountTypeChange(
              (e.target.value === 'NA' ? null : e.target.value) as PointCountType | null)}
          >
            <option value="NA">NA</option>
            <option value="400">400</option>
            <option value="1000">1000</option>
          </select>
        </label>

        {/* Prototype v8: a SMALL SOLID orange "PC Entry…" — the one primary
            action on this strip — next to the ghost + Add / Remove. */}
        <button
          type="button"
          className="ca-btn ca-btn--sm ca-btn-solid"
          disabled={readOnly || pointCountDisabled || pointCountType === null}
          onClick={onOpenPointCount}
          title={pointCountDisabled
            ? 'Save the layer before entering a point count'
            // Alt+T, bound by the page — NOT Alt+P, which is Preview's reserved
            // key (the two collided until 2026-09-26).
            : `Enter the per-slide point count (${formatCombo(effectiveCombo('alt+t'))})`}
        >
          PC Entry…
        </button>

        {toolbarExtra}

        {pointCountDisabled && (
          <span className="plm-pc-bar__hint">Save the layer first.</span>
        )}
      </div>

      <div className="plm-exam__grid">
        {/* A component list is a handful of rows being EDITED, not a dataset
            being browsed, so it carries no pager — the prototype's grid has
            none either, and a "Page 1 of 1" under four rows is just noise.
            The leading checkbox mirrors the prototype and marks the row the
            toolbar's Remove acts on. */}
        <AppDataGrid
          rows={exams}
          columns={columns}
          getRowId={(_, i) => i}
          selectedRowId={activeIndex}
          onRowClick={r => onActiveIndexChange(exams.indexOf(r))}
          selectable
          selectedIds={new Set(active ? [activeIndex] : [])}
          onSelectionChange={next => {
            const picked = [...next].find(id => id !== activeIndex);
            if (typeof picked === 'number') onActiveIndexChange(picked);
          }}
          hideFooter
          defaultPageSize={999}
          emptyText="No fibers or components recorded yet — use + Add."
        />
        {/* Visual-estimate total (prototype v8's grid footer). The components
            of a layer should add to 100%; showing the running total where the
            analyst is typing is how they notice they are at 92% before they
            submit. Amber when it is not 100, because a layer that does not add
            up is the single most common entry mistake. */}
        <div className="plm-exam__total">
          <span className="plm-exam__totallabel">Visual-estimate total</span>
          <span className={`plm-exam__totalvalue${examTotal.balanced ? '' : ' is-off'}`}>
            {examTotal.text}
          </span>
        </div>
        {/* Inline quick-add (prototype v8): "+ Add a component — Asbestos
            Fibers · Non-Asbestos Fibers · …". Each type is a one-click add
            that lands the row with its Component already chosen, which is the
            first thing you would pick anyway. The types come from the
            FiberTypes lookup, not a hardcoded list, so a master the lab adds
            appears here too. */}
        {!readOnly && lookups.fiberTypes.length > 0 && (
          <div className="plm-quickadd">
            <span className="plm-quickadd__plus" aria-hidden="true">+</span>
            <span className="plm-quickadd__lead">Add a {ROW_WORD} —</span>
            {lookups.fiberTypes.map((t, i) => (
              <span key={t.id}>
                {i > 0 && <span className="plm-quickadd__sep" aria-hidden="true">·</span>}
                <button
                  type="button"
                  className="plm-quickadd__btn"
                  onClick={() => addWithType(t.id)}
                  title={`Add a ${t.name ?? ROW_WORD} row`}
                >{t.name ?? '—'}</button>
              </span>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* The layer's ANSWER, stated once, between the grid that produced it and
          the optics that describe it (prototype v8 `#resultBar`). An analyst
          reading a filled grid should not have to re-derive "so what does this
          layer report?" from the rows. */}
      <div className="plm-resultbar">
        <span className="plm-resultbar__main">
          Layer result: <b>{layerResult}</b>
        </span>
        <span className={`plm-resultbar__how plm-resultbar__how--${
          pointCountType ? 'count' : 'visual'}`}>
          {pointCountType
            ? `Point count ${pointCountType}`
            : 'Visual estimate'}
        </span>
      </div>

      {active ? (
        <>
        {/* Prototype v8's `.optics-hd`: a plain bold line above the cards —
            "Optical properties · for {name} · [Typical: …]" all on ONE row —
            not a panel header strip with a card drawn round a grid of cards. */}
        <div className="plm-optics-hd">
          <span className="plm-optics-hd__lead">Optical properties</span>
          <span className="plm-optics-hd__for">{opticsFor}</span>
          {typical && (
            <span className="plm-typical"
              title="From the FiberTypesValues master — reference only, never filled in for you">
              Typical: {typical}
            </span>
          )}
        </div>
        <div className="plm-exam__detail">
          <section className="plm-group">
            <h4 className="plm-group__title">Birefringence</h4>
            <div className="plm-checks plm-checks--2">{BIREFRINGENCE.map(([k, l]) => check(k, l))}</div>
            <p className="plm-hint">Pick one.</p>
          </section>

          <section className="plm-group">
            <h4 className="plm-group__title">Pleochroism</h4>
            <div className="plm-checks plm-checks--2">
              {check('pleochroismYes', 'Yes')}
              {check('pleochroismNo', 'No')}
            </div>
            {gatedText('pleochroismColor', 'pleochroismYes', 'Color')}
            <p className="plm-hint">Pick one.</p>
          </section>

          <section className="plm-group">
            <h4 className="plm-group__title">Morphology</h4>
            <div className="plm-checks plm-checks--4">
              {MORPHOLOGY.map(([k, l]) => check(k, l))}
              {/* "Other" has no bit column on dbo.PLMExam — Morphology_Text is
                  the only storage — so the TEXT is the state: ticked when it
                  has content, and unticking clears it. That gives the
                  prototype's option without inventing a column. */}
              <label className="plm-chk">
                <input
                  type="checkbox"
                  checked={!!active?.morphologyText}
                  disabled={readOnly || !active}
                  onChange={e => setActive('morphologyText',
                    (e.target.checked ? ' ' : null) as PlmExamInput['morphologyText'])}
                />
                <span>Other</span>
              </label>
            </div>
            <input
              type="text"
              className="plm-input plm-othr"
              placeholder="Other…"
              aria-label="Other morphology"
              value={(active?.morphologyText ?? '').trimStart()}
              disabled={readOnly || !active || !active.morphologyText}
              title={active?.morphologyText ? undefined : 'Tick "Other" to describe it'}
              onChange={e => setActive('morphologyText',
                (e.target.value || ' ') as PlmExamInput['morphologyText'])}
            />
          </section>

          <section className="plm-group">
            <h4 className="plm-group__title">Sign of Elongation</h4>
            <div className="plm-checks plm-checks--3">{ELONGATION.map(([k, l]) => check(k, l))}</div>
            <p className="plm-hint">Pick one.</p>
          </section>

          <section className="plm-group">
            <h4 className="plm-group__title">Extinction</h4>
            <div className="plm-checks plm-checks--3">{EXTINCTION.map(([k, l]) => check(k, l))}</div>
          </section>

          <section className="plm-group">
            <h4 className="plm-group__title">Refractive Index</h4>
            <div className="plm-rigrid">
              {riField('gamma', 'Gamma (γ)', '1.xxx')}
              {riField('alpha', 'Alpha (α)', '1.xxx')}
              {riField('riOil', 'RI Oil', '1.550')}
              {/* No λ Parallel / λ Perpendicular inputs (owner 2026-10-20): the wavelengths
                  are not used. A stored value is kept as it is, just not shown. */}
            </div>
          </section>
        </div>
        </>
      ) : (
        <div className="plm-exam__empty">
          Select a row above to edit its optical properties.
        </div>
      )}
    </div>
  );
}
